import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format, formatDistanceToNow } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './TransporterProfile.css';

const CATEGORY_LABELS = {
  punctuality: '⏱ Punctuality',
  communication: '💬 Communication',
  packaging_care: '📦 Packaging care',
  reliability: '🤝 Reliability',
  value_for_money: '💶 Value for money',
};

function StatCard({ value, label }) {
  return (
    <div className="tp-stat">
      <div className="tp-stat-value">{value}</div>
      <div className="tp-stat-label">{label}</div>
    </div>
  );
}

export default function TransporterProfile() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchAll();
  }, [id, user, authLoading]);

  async function fetchAll() {
    setLoading(true);
    const [profileRes, reviewsRes, tripsRes, bookingsRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', id).single(),
      supabase.from('reviews')
        .select('*')
        .eq('reviewee_id', id)
        .eq('reviewer_role', 'sender')
        .order('created_at', { ascending: false }),
      supabase.from('trips')
        .select('*')
        .eq('transporter_id', id)
        .eq('status', 'active')
        .gte('departure_date', new Date().toISOString())
        .order('departure_date', { ascending: true })
        .limit(5),
      supabase.from('bookings')
        .select('status, trips!inner(transporter_id)')
        .eq('trips.transporter_id', id),
    ]);

    const p = profileRes.data;

    if (!p || p.role === 'sender') {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setProfile(p);

    const reviewData = reviewsRes.data || [];
    setReviews(reviewData);
    setTrips(tripsRes.data || []);

    const avgRating = reviewData.length > 0
      ? reviewData.reduce((sum, r) => sum + r.rating, 0) / reviewData.length
      : null;

    const categoryAvgs = {};
    Object.keys(CATEGORY_LABELS).forEach(key => {
      const vals = reviewData.filter(r => r[key]).map(r => r[key]);
      if (vals.length > 0) {
        categoryAvgs[key] = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
      }
    });

    const allBookings = bookingsRes.data || [];
    const total = allBookings.length;
    const confirmed = allBookings.filter(b => !['cancelled', 'pending'].includes(b.status)).length;
    const delivered = allBookings.filter(b => b.status === 'delivered').length;
    const acceptanceRate = total > 0 ? Math.round((confirmed / total) * 100) : null;

    setStats({ total, delivered, acceptanceRate, reviewCount: reviewData.length, avgRating, categoryAvgs });
    setLoading(false);
  }

  if (authLoading || loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (notFound) return (
    <div className="page">
      <div className="container" style={{ textAlign: 'center', paddingTop: '80px' }}>
        <div style={{ fontSize: '48px' }}>🚫</div>
        <h2 style={{ margin: '16px 0 8px' }}>Profile not available</h2>
        <p style={{ color: 'var(--olive)' }}>This user does not have a public transporter profile.</p>
        <Link to="/search" className="btn btn-primary" style={{ marginTop: '24px', display: 'inline-block' }}>Back to search</Link>
      </div>
    </div>
  );

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const memberSince = profile.created_at ? format(new Date(profile.created_at), 'MMMM yyyy') : null;

  return (
    <div className="page tp-page">
      <div className="container">
        <Link to="/search" className="back-link">← Back to search</Link>

        {/* HERO */}
        <div className="card tp-hero" style={{ position: 'relative' }}>
          <div className="tp-hero-left">
            <div className="tp-avatar">{profile.full_name?.[0] || '?'}</div>
            <div className="tp-hero-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <h1 className="tp-name">{profile.full_name}</h1>
                {profile.is_verified && <span className="badge badge-green">✓ Verified</span>}
                <span className="badge badge-gray">✈️ Transporter</span>
              </div>
              {profile.city && (
                <div style={{ color: 'var(--olive)', fontSize: '14px', marginTop: '4px' }}>📍 {profile.city}, Germany</div>
              )}
              {memberSince && (
                <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '4px' }}>🗓 Member since {memberSince}</div>
              )}
              {stats?.avgRating != null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <span style={{ color: '#F5A623', fontSize: '18px' }}>{stars(stats.avgRating)}</span>
                  <span style={{ fontWeight: '700', fontSize: '16px' }}>{stats.avgRating.toFixed(1)}</span>
                  <span style={{ color: 'var(--olive)', fontSize: '13px' }}>({stats.reviewCount} review{stats.reviewCount !== 1 ? 's' : ''})</span>
                </div>
              ) : (
                <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '8px' }}>No reviews yet</div>
              )}
              {profile.bio && (
                <p style={{ marginTop: '12px', fontSize: '14px', color: 'rgba(245,237,216,0.7)', lineHeight: '1.6', maxWidth: '500px' }}>
                  {profile.bio}
                </p>
              )}
            </div>
          </div>
          {profile.role === 'both' && (
            <Link to={`/sender/${id}`} className="btn btn-ghost" style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '12px', padding: '6px 12px' }}>
              View as Sender →
            </Link>
          )}
        </div>

        {/* STATS */}
        {stats && (
          <div className="tp-stats">
            <StatCard value={stats.delivered} label={t('transporterProfile.tripsCompleted')} />
            <StatCard value={stats.total} label={t('transporterProfile.totalBookings')} />
            <StatCard value={stats.acceptanceRate !== null ? `${stats.acceptanceRate}%` : '—'} label={t('transporterProfile.acceptanceRate')} />
            <StatCard value={stats.reviewCount} label={t('transporterProfile.reviews')} />
          </div>
        )}

        <div className="tp-layout">
          <div className="tp-left">
            {stats?.categoryAvgs && Object.keys(stats.categoryAvgs).length > 0 && (
              <div className="card tp-categories">
                <h3>{t('transporterProfile.ratingBreakdown')}</h3>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
                  stats.categoryAvgs[key] ? (
                    <div key={key} className="tp-cat-row">
                      <span className="tp-cat-label">{label}</span>
                      <div className="tp-cat-bar-wrap">
                        <div className="tp-cat-bar">
                          <div className="tp-cat-fill" style={{ width: `${(stats.categoryAvgs[key] / 5) * 100}%` }} />
                        </div>
                        <span className="tp-cat-val">{stats.categoryAvgs[key]}</span>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            )}

            <div className="card" style={{ marginTop: '16px' }}>
              <h3 style={{ marginBottom: '20px' }}>Reviews ({reviews.length})</h3>
              {reviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--olive)', fontSize: '14px' }}>
                  No reviews yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {reviews.map(r => (
                    <div key={r.id} style={{ paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: 'white', flexShrink: 0 }}>✓</div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Verified sender</div>
                            <div style={{ fontSize: '11px', color: 'var(--olive)' }}>
                              {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                            </div>
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#F5A623', fontSize: '14px' }}>{stars(r.rating)}</span>
                          <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '6px' }}>{r.rating}/5</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '8px 0' }}>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
                          r[key] ? (
                            <div key={key} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '3px 8px' }}>
                              {label} <span style={{ color: '#F5A623' }}>{'★'.repeat(r[key])}</span>
                            </div>
                          ) : null
                        )}
                      </div>
                      {r.comment && (
                        <p style={{ fontSize: '14px', color: 'rgba(245,237,216,0.7)', margin: '8px 0 0', lineHeight: '1.5' }}>
                          "{r.comment}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="tp-right">
            <div className="card">
              <h3 style={{ marginBottom: '16px' }}>Upcoming trips ({trips.length})</h3>
              {trips.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--olive)', fontSize: '14px' }}>
                  No active trips at the moment.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {trips.map(t => {
                    const pct = Math.round(((t.total_kg - t.available_kg) / t.total_kg) * 100);
                    const originFlag = t.origin_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
                    const destFlag = t.destination_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
                    return (
                      <Link to={`/trip/${t.id}`} key={t.id} className="tp-trip-card">
                        <div className="tp-trip-route">
                          {originFlag} {t.origin_city} → {destFlag} {t.destination_city}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--olive)', margin: '4px 0' }}>
                          {format(new Date(t.departure_date), 'MMM d, yyyy')} · €{t.price_per_kg}/kg · {t.available_kg} kg left
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                          <div className="capacity-bar" style={{ height: '4px', flex: 1 }}>
                            <div className="capacity-fill" style={{ width: `${pct}%` }} />
                          </div>
                          <span style={{ fontSize: '11px', color: 'var(--olive)', whiteSpace: 'nowrap' }}>{pct}% booked</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}